<template>
  <div class="formatted-text">
    <div
      ref="inner"
      v-html="renderedText"
      v-on="isTruncated ? { click: showRestOfText } : {}"
    ></div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import lineClamp from 'line-clamp'
import { makeHtmlSafe, resolveLineBreakOfLastWord } from '@/helpers/string'
import { t } from '@/config/vue-i18n'

const READ_MORE = {
  text: t('global.read_more'),
  class: 'formatted-text__read-more',
  classInner: 'formatted-text__read-more-inner',
}
const ELLIPSIS_CHAR = `… ${READ_MORE.text}`

export default {
  name: 'FormattedText',

  props: {
    /**
     * Text to be formatted.
     */
    text: {
      type: String,
      required: true,
    },
    /**
     * Number of line to show before truncating the text. If undefined, the text won't be truncated.
     */
    visibleLineCount: {
      type: Number,
    },
    /**
     * Options passed to resolveLineBreakOfLastWord helper.
     */
    options: {
      type: Object,
    },
  },

  data() {
    return {
      renderedText: '',
      isTruncated: false,
      isShowingRestOfText: false,
    }
  },

  computed: {
    ...mapGetters({ viewportWidth: 'viewport/width' }),

    formattedText() {
      /**
       * Transforms subsequent line breaks into paragraphs and single line break into <br> tag.
       * It will also make sure that no word is left alone on the last line.
       */
      const safeCopy = makeHtmlSafe(this.text)
      const lineBreakRegex = /\s*[\r\n]+(\s+[\r\n]+)*\s*/g
      const openingParagraph = '<p class="formatted-text__paragraph">'
      let startIndex = 0
      let match
      let formattedCopy = openingParagraph

      while ((match = lineBreakRegex.exec(safeCopy)) !== null) {
        const rLineBreakMatch = match[0].match(/\r/g)
        const nLineBreakMatch = match[0].match(/\n/g)
        const rLineBreakCount = rLineBreakMatch ? rLineBreakMatch.length : 0
        const nLineBreakCount = nLineBreakMatch ? nLineBreakMatch.length : 0
        const lineBreakCount = Math.max(rLineBreakCount, nLineBreakCount)

        formattedCopy += resolveLineBreakOfLastWord(
          safeCopy.substring(startIndex, match.index),
          this.options
        )
        formattedCopy += lineBreakCount > 1 ? `</p>${openingParagraph}` : '<br>'
        startIndex = match.index + match[0].length
      }
      formattedCopy +=
        resolveLineBreakOfLastWord(safeCopy.substring(startIndex), this.options) + '</p>'

      return formattedCopy
    },
  },

  watch: {
    viewportWidth: {
      immediate: true,
      handler() {
        this.renderedText = this.formattedText
        if (this.visibleLineCount) this.truncateText()
      },
    },
  },

  methods: {
    async truncateText() {
      if (this.isShowingRestOfText) return

      await this.$nextTick()
      // The innerHTML won't be updated after $nextTick nor get updated with this.$forceUpdate().
      // We need to update it manually since lineClamp depends on it.
      this.$refs.inner.innerHTML = this.renderedText
      lineClamp(this.$refs.inner, this.visibleLineCount, { ellipsis: ELLIPSIS_CHAR })

      this.$refs.inner.innerHTML = this.$refs.inner.innerHTML.replace(
        new RegExp(`(${READ_MORE.text})(</[^>]+>)*$`),
        (m, readMoreText, endTag) => {
          this.isTruncated = true
          return (
            `<span class="${READ_MORE.class}">` +
            `<a class="${READ_MORE.classInner}">${readMoreText}</a>` +
            `</span>${endTag}`
          )
        }
      )
      this.renderedText = this.$refs.inner.innerHTML
    },

    showRestOfText(event) {
      const readMoreClasses = [READ_MORE.class, READ_MORE.classInner]
      const isReadMore = readMoreClasses.some(cls => event.target.matches(`.${cls}`))

      if (isReadMore) {
        this.renderedText = this.formattedText
        this.isShowingRestOfText = true
      }
    },
  },
}
</script>

<style lang="scss" scoped>
$read-more-vert-padding: 6px;
$read-more-hor-padding: 12px;

.formatted-text {
  /deep/ &__paragraph {
    display: block;
    padding: 5px 0;

    &:first-child {
      padding-top: 0;
    }

    &:last-child {
      padding-bottom: 0;
    }
  }

  /deep/ &__read-more {
    display: inline-block;
    position: relative;

    &:before {
      content: '\00a0'; // space
      position: static;
    }
  }

  /deep/ &__read-more-inner {
    position: absolute;
    top: 0;
    left: 0;
    padding: $read-more-vert-padding $read-more-hor-padding;
    color: $color-text;
    text-decoration: underline;
    white-space: nowrap;
    cursor: pointer;
    transform: translate(-$read-more-hor-padding, -$read-more-vert-padding);

    &:hover {
      color: $color-text-light;
    }
  }
}
</style>
